import { useCallback, useEffect, useRef, useState } from "react";

const DEFAULT_WIDTH = 400;
const DEFAULT_HEIGHT = 557;
const MIN_SIZE = 120;
const MAX_SIZE = 700;

function ImageCropper({ imageUrl, onCrop, onCancel, onReset }) {
  const canvasRef = useRef(null);
  const imgRef = useRef(null);

  const [imgLoaded, setImgLoaded] = useState(false);
  const [drawError, setDrawError] = useState("");
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [saving, setSaving] = useState(false);

  const [cropW, setCropW] = useState(DEFAULT_WIDTH);
  const [cropH, setCropH] = useState(DEFAULT_HEIGHT);
  const [resizing, setResizing] = useState(null);
  const resizeStartRef = useRef(null);

  useEffect(() => {
    setImgLoaded(false);
    setDrawError("");
    setZoom(1);
    setOffset({ x: 0, y: 0 });

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      imgRef.current = img;

      // Initialise le cropper aux dimensions réelles de l'image (÷2 car sauvegardée en retina)
      const initW = Math.round(
        Math.min(MAX_SIZE, Math.max(MIN_SIZE, img.naturalWidth / 2)),
      );
      const initH = Math.round(
        Math.min(MAX_SIZE, Math.max(MIN_SIZE, img.naturalHeight / 2)),
      );
      setCropW(initW);
      setCropH(initH);

      setImgLoaded(true);
    };
    img.onerror = () => {
      imgRef.current = null;
      setImgLoaded(false);
      setDrawError(
        "Impossible de charger cette image pour le recadrage. Vérifiez l'URL ou l'hébergement.",
      );
    };
    img.src = imageUrl;
  }, [imageUrl]);

  const drawPreview = useCallback(() => {
    const canvas = canvasRef.current;
    const img = imgRef.current;
    if (!canvas || !img) return;

    canvas.width = cropW * 2;
    canvas.height = cropH * 2;

    const ctx = canvas.getContext("2d");
    const cw = canvas.width;
    const ch = canvas.height;

    ctx.clearRect(0, 0, cw, ch);
    ctx.fillStyle = "#120e0c";
    ctx.fillRect(0, 0, cw, ch);

    const imgRatio = img.naturalWidth / img.naturalHeight;
    const canvasRatio = cw / ch;

    let drawW, drawH;
    if (imgRatio > canvasRatio) {
      drawH = ch * zoom;
      drawW = drawH * imgRatio;
    } else {
      drawW = cw * zoom;
      drawH = drawW / imgRatio;
    }

    const dx = (cw - drawW) / 2 + offset.x * 2;
    const dy = (ch - drawH) / 2 + offset.y * 2;

    try {
      ctx.drawImage(img, dx, dy, drawW, drawH);
      if (drawError) {
        setDrawError("");
      }
    } catch (_error) {
      setDrawError(
        "Cette image ne peut pas être dessinée dans le recadreur (restriction CORS).",
      );
    }
  }, [zoom, offset, cropW, cropH, drawError]);

  useEffect(() => {
    if (imgLoaded) {
      drawPreview();
    }
  }, [imgLoaded, drawPreview]);

  // --- Image drag (pan) ---
  const handlePointerDown = (e) => {
    if (resizing) return;
    e.preventDefault();
    setDragging(true);
    const point = e.touches ? e.touches[0] : e;
    setDragStart({ x: point.clientX - offset.x, y: point.clientY - offset.y });
  };

  const handlePointerMove = (e) => {
    if (!dragging || resizing) return;
    e.preventDefault();
    const point = e.touches ? e.touches[0] : e;
    setOffset({
      x: point.clientX - dragStart.x,
      y: point.clientY - dragStart.y,
    });
  };

  const handlePointerUp = () => {
    setDragging(false);
  };

  // --- Resize handles ---
  const handleResizeStart = (handle, e) => {
    e.preventDefault();
    e.stopPropagation();
    const point = e.touches ? e.touches[0] : e;
    resizeStartRef.current = {
      x: point.clientX,
      y: point.clientY,
      w: cropW,
      h: cropH,
    };
    setResizing(handle);
  };

  useEffect(() => {
    if (!resizing) return;

    const onMove = (e) => {
      const point = e.touches ? e.touches[0] : e;
      const start = resizeStartRef.current;
      if (!start) return;

      const dx = point.clientX - start.x;
      const dy = point.clientY - start.y;

      let newW = start.w;
      let newH = start.h;

      if (resizing.includes("r")) newW = start.w + dx;
      if (resizing.includes("l")) newW = start.w - dx;
      if (resizing.includes("b")) newH = start.h + dy;
      if (resizing.includes("t")) newH = start.h - dy;

      // Diagonal corners: resize proportionally
      if (
        (resizing === "br" ||
          resizing === "tl" ||
          resizing === "bl" ||
          resizing === "tr") &&
        resizing.length === 2
      ) {
        const diagonal = Math.sqrt(dx * dx + dy * dy);
        const sign =
          (resizing === "br" && (dx > 0 || dy > 0)) ||
          (resizing === "tl" && (dx < 0 || dy < 0)) ||
          (resizing === "tr" && (dx > 0 || dy < 0)) ||
          (resizing === "bl" && (dx < 0 || dy > 0))
            ? 1
            : -1;
        const ratio = start.w / start.h;
        const delta = sign * diagonal;
        newW = start.w + delta;
        newH = newW / ratio;
      }

      newW = Math.round(Math.min(MAX_SIZE, Math.max(MIN_SIZE, newW)));
      newH = Math.round(Math.min(MAX_SIZE, Math.max(MIN_SIZE, newH)));

      setCropW(newW);
      setCropH(newH);
    };

    const onUp = () => {
      setResizing(null);
      resizeStartRef.current = null;
    };

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
    window.addEventListener("touchcancel", onUp);

    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
      window.removeEventListener("touchcancel", onUp);
    };
  }, [resizing]);

  const handleSave = async () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    setSaving(true);
    try {
      // Force redraw to ensure the canvas matches current crop dimensions
      drawPreview();

      const blob = await new Promise((resolve, reject) => {
        try {
          canvas.toBlob((b) => resolve(b), "image/jpeg", 0.88);
        } catch (err) {
          reject(err);
        }
      });
      if (blob) {
        await onCrop(blob);
      }
    } catch (_error) {
      // Tainted canvas or other error — silently handled by parent
    } finally {
      setSaving(false);
    }
  };

  const handles = ["t", "b", "l", "r", "tl", "tr", "bl", "br"];

  return (
    <div className="image-cropper-overlay">
      <div className="image-cropper-modal">
        <h3 className="image-cropper-title">Recadrer / Redimensionner</h3>

        <p className="image-cropper-hint">
          Glissez l'image pour la repositionner. Tirez les poignées pour
          redimensionner (coins = diagonal, bords = largeur ou hauteur).
        </p>

        <div className="image-cropper-resize-area">
          <div
            className="image-cropper-canvas-wrap"
            style={{ width: cropW, height: cropH }}
          >
            {imgLoaded ? (
              <canvas
                ref={canvasRef}
                width={cropW * 2}
                height={cropH * 2}
                style={{ width: cropW, height: cropH }}
                onMouseDown={handlePointerDown}
                onMouseMove={handlePointerMove}
                onMouseUp={handlePointerUp}
                onMouseLeave={handlePointerUp}
                onTouchStart={handlePointerDown}
                onTouchMove={handlePointerMove}
                onTouchEnd={handlePointerUp}
                onTouchCancel={handlePointerUp}
              />
            ) : (
              <p className="image-cropper-loading">Chargement de l'image...</p>
            )}

            {imgLoaded &&
              handles.map((h) => (
                <div
                  key={h}
                  className={`image-cropper-handle image-cropper-handle-${h}`}
                  onMouseDown={(e) => handleResizeStart(h, e)}
                  onTouchStart={(e) => handleResizeStart(h, e)}
                />
              ))}
          </div>

          {drawError ? <p className="image-cropper-error">{drawError}</p> : null}

          <p className="image-cropper-size-info">
            {cropW} × {cropH} px
          </p>
        </div>

        <div className="image-cropper-controls">
          <label className="image-cropper-zoom-label">
            Zoom
            <input
              type="range"
              min="1"
              max="3"
              step="0.05"
              value={zoom}
              onChange={(e) => setZoom(parseFloat(e.target.value))}
              className="image-cropper-zoom-slider"
            />
            <span className="image-cropper-zoom-value">
              {Math.round(zoom * 100)}%
            </span>
          </label>
        </div>

        <div className="image-cropper-actions">
          <button
            type="button"
            className="refresh-btn"
            onClick={onCancel}
            disabled={saving}
          >
            Annuler
          </button>
          {onReset ? (
            <button
              type="button"
              className="refresh-btn image-cropper-reset-btn"
              onClick={onReset}
              disabled={saving}
            >
              Réinitialiser (image d'origine)
            </button>
          ) : null}
          <button
            type="button"
            className="refresh-btn reservation-open-create-btn"
            onClick={handleSave}
            disabled={!imgLoaded || saving || Boolean(drawError)}
          >
            {saving ? "Sauvegarde..." : "Sauvegarder le recadrage"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default ImageCropper;
